import { useCallback, useMemo } from 'react';
import { orderBy } from 'lodash';
import { COMPANY_NOT_FOUND } from 'common/constants/auth';
import { PINNED_COMPANIES_KEY } from 'common/constants/userPreferences';
import { useLocalStorageByUser } from 'common/hooks';
import { Text } from 'components/ui';
import { Company } from 'pages/Auth/components/CompanySelector/components/Company';
import { CompaniesListProps } from './CompaniesList.props';

const CompaniesList = ({ filteredCompaniesList }: CompaniesListProps) => {
  const [pinnedCompanies, setPinnedCompanies] = useLocalStorageByUser(PINNED_COMPANIES_KEY, []) as [
    { label: string; value: number }[],
    (value: { label: string; value: number }[]) => void,
  ];

  const updatePinStatus = useCallback(
    (id: number, title: string, isPinned: boolean) => {
      let nextPinnedOptions: { label: string; value: number }[];

      if (isPinned) {
        nextPinnedOptions = pinnedCompanies.filter(option => option.value !== id);
      } else {
        nextPinnedOptions = [...pinnedCompanies, { value: id, label: title }];
      }

      setPinnedCompanies(nextPinnedOptions);
    },
    [pinnedCompanies, setPinnedCompanies]
  );

  const processedCompanies = useMemo(
    () =>
      filteredCompaniesList.map(company => {
        const isCompanyPinned = Boolean(pinnedCompanies.find(pinnedCompany => pinnedCompany.value === company.id));

        return {
          ...company,
          isPinned: isCompanyPinned,
          isPinnable: true,
          updatePinStatus: (id: number, title: string, isPinned: boolean) => updatePinStatus(id, title, isPinned),
        };
      }),
    [filteredCompaniesList, pinnedCompanies, updatePinStatus]
  );

  const sortedCompanies = useMemo(() => orderBy(processedCompanies, ['isPinned'], ['desc']), [processedCompanies]);

  if (filteredCompaniesList.length === 0) {
    return <Text variant="h4">{COMPANY_NOT_FOUND.toUpperCase()}</Text>;
  }

  return (
    <>
      {sortedCompanies.map(company => (
        <Company
          key={company.id}
          id={company.id}
          isPinned={company.isPinned}
          isPinnable={company.isPinnable}
          name={company.name}
          slug={company.slug}
          updatePinStatus={company.updatePinStatus}
        />
      ))}
    </>
  );
};

export default CompaniesList;
